/* SPDX-License-Identifier: AGPL-3.0-or-later */

import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  QueryList,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { QualificationTeamScoreDto } from '@api';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/overlay';
import {
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { fromEvent, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-pick-qualified-teams',
  standalone: true,
  imports: [
    CdkDropList,
    CdkDrag,
    CdkScrollable,
    MatDialogTitle,
    MatDialogContent,
    MatDivider,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCell,
    MatCellDef,
    MatIcon,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRowDef,
    MatRow,
    MatDialogActions,
    MatButton,
  ],
  templateUrl: './pick-qualified-teams.component.html',
  styleUrl: './pick-qualified-teams.component.scss',
})
export class PickQualifiedTeamsComponent implements AfterViewInit, OnDestroy {
  private unsubscribe$ = new Subject();

  dialogRef = inject(MatDialogRef<PickQualifiedTeamsComponent>);

  guaranteedQualifiedTeams = signal<QualificationTeamScoreDto[]>([]);
  edgeTeamsWithSamePoints = signal<QualificationTeamScoreDto[]>([]);
  guaranteedNotQualifiedTeams = signal<QualificationTeamScoreDto[]>([]);

  matTable = viewChild<MatTable<QualificationTeamScoreDto>>('dragAndDropTable');
  infoElement = viewChild<ElementRef>('qualifiedInfo');
  renderedRows = viewChildren<QueryList<ElementRef<HTMLTableRowElement>>>(
    'dragAndDropTableRow',
    // @ts-expect-error - this is a bug in the typescript definitions
    {
      static: false,
      read: ElementRef,
    },
  );

  displayedColumns = ['position', 'name', 'wins', 'points', 'dragIcon'];

  ngAfterViewInit(): void {
    this.positionInfo();
    fromEvent(window, 'resize')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.positionInfo();
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  drop(event: CdkDragDrop<string[]>) {
    const edgeTeams = this.edgeTeamsWithSamePoints();
    moveItemInArray(edgeTeams, event.previousIndex, event.currentIndex);
    this.edgeTeamsWithSamePoints.set(edgeTeams);
    if (this.matTable()) {
      this.matTable()!.renderRows();
    }
  }

  printQualifiedTeams() {
    const teams = this.guaranteedQualifiedTeams()
      .concat(this.edgeTeamsWithSamePoints())
      .slice(0, 16)
      .map(team => team.id);
    this.dialogRef.close(teams);
  }

  asScoreDto(team: QualificationTeamScoreDto): QualificationTeamScoreDto {
    return team;
  }

  positionInfo() {
    const info = this.infoElement()!.nativeElement;
    const row = (this.renderedRows() as ElementRef[])[0]?.nativeElement;
    if (!row) {
      return;
    }

    const indexOfLastEdgeTeam = 16 - this.guaranteedQualifiedTeams().length;

    info.style.left = `${row.offsetLeft + row.offsetWidth}px`;
    info.style.top = `${-info.offsetHeight / 2 + row.offsetHeight * indexOfLastEdgeTeam}px`;
  }
}
